import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { MatChipsModule } from '@angular/material/chips';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { AutomationReplacementChipsComponent } from '../../../../common/components';
import { StaticSelectorService } from '../../../../data-services/static-selector.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const sleepMonthDays = 'sleepMonthDays';

@Component({
  selector: 'automata-monthday-selector',
  template: `
    <glxy-form-field>
      <automata-automation-replacement-chips
        [dataService]="staticSelectorService"
        [formControl]="formControl"
        [isRequired]="true"
        [multiple]="true"
        [supportDataPassing]="false"
      ></automata-automation-replacement-chips>
    </glxy-form-field>
    <ng-container *ngIf="isMonthEndDaySelected()">
      <glxy-alert type="warning">
        <strong>{{ 'AUTOMATIONS.EDITOR.TASKS.DELAY.WARNING_NOT_ALL_MONTHS' | translate }}</strong>
        <glxy-alert-extended>
          {{ 'AUTOMATIONS.EDITOR.TASKS.DELAY.ADVICE_CHOOSE_MONTH_END' | translate }}
        </glxy-alert-extended>
      </glxy-alert>
    </ng-container>
    <ng-container *ngIf="isSelected(32)">
      <glxy-alert type="info">
        <strong>{{ 'AUTOMATIONS.EDITOR.TASKS.DELAY.INFO_MONTH_END_SELECTED' | translate }}</strong>
        <glxy-alert-extended>
          {{ 'AUTOMATIONS.EDITOR.TASKS.DELAY.WILL_RUN_AT_END_OF_MONTH' | translate }}
        </glxy-alert-extended>
      </glxy-alert>
    </ng-container>
  `,
  styleUrls: ['./monthday-selector.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    MatChipsModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyInputModule,
    GalaxyAlertModule,
    AutomationReplacementChipsComponent,
  ],
})
export class MonthDaySelectorComponent implements OnInit {
  @Input() form: UntypedFormGroup;
  @Input() sleepMonthDays: number[];

  formControl?: FormControl;

  chips = Array.from({ length: 32 }, (_, index) => index + 1);

  staticSelectorService = new StaticSelectorService<number>(
    new Map(
      this.chips.map((n) => {
        if (n === 32) {
          return [n, 'AUTOMATIONS.EDITOR.TASKS.DELAY.MONTH_END'];
        }
        return [n, n.toString()];
      }),
    ),
    this.translateService,
  );

  constructor(
    private readonly changes: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.formControl = new FormControl<number[]>(this.sleepMonthDays);
    this.form.addControl(sleepMonthDays, this.formControl);
    this.formControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(this.onClick);
  }

  isSelected(chip: any) {
    const monthdays: number[] = this.form.get(sleepMonthDays)?.value || [];
    return monthdays.includes(chip);
  }

  onClick(chip: number) {
    let monthdays: number[] = this.form.get(sleepMonthDays)?.value || [];
    if (monthdays.includes(chip)) {
      monthdays = monthdays.filter((v) => v !== chip);
    } else {
      monthdays = [...monthdays, chip];
      if (chip === 32) {
        monthdays = monthdays.filter((v) => v < 28 || v > 31);
      }
    }

    this.form.get(sleepMonthDays)!.setValue(monthdays);
    this.changes.detectChanges();
  }

  isMonthEndDaySelected() {
    return [29, 30, 31].some((v: number) => this.isSelected(v)) && !this.isSelected(32);
  }
}
